import { useCallback, useState } from 'react';

import { useEquivalentProductMatching } from '~/core/hooks/useEquivalentProductMatching/useEquivalentProductMatching';
import { StoreModel } from '~/core/models';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { selectSelectedStore, updateSelectedStore } from '~/redux/slices/store';
import { useDrawingShapeContext } from '~contexts';

export const useStoreSelection = () => {
  const [storeCandidate, setStoreCandidate] = useState<StoreModel | undefined>(
    undefined
  );
  const [
    isChangeStoreConfirmationModalOpen,
    setIsChangeStoreConfirmationModalOpen,
  ] = useState(false);

  const dispatch = useAppDispatch();
  const selectedStore = useAppSelector(selectSelectedStore);
  const { replaceProductsWithEquivalents } = useEquivalentProductMatching();

  const {
    getters: { areThereShapesInTheDrawingArea },
  } = useDrawingShapeContext();

  const handleStoreSelection = useCallback(
    (store: StoreModel) => () => {
      if (store.companyId === selectedStore?.companyId) {
        return;
      }

      if (areThereShapesInTheDrawingArea && selectedStore) {
        setStoreCandidate(store);
        setIsChangeStoreConfirmationModalOpen(true);
      } else {
        dispatch(updateSelectedStore(store));
      }
    },
    [selectedStore, areThereShapesInTheDrawingArea, dispatch]
  );

  const confirmStoreChange = useCallback(async () => {
    if (storeCandidate) {
      await replaceProductsWithEquivalents({
        storeId: storeCandidate.companyId,
      });
    }

    dispatch(updateSelectedStore(storeCandidate));
    setIsChangeStoreConfirmationModalOpen(false);
    setStoreCandidate(undefined);
  }, [storeCandidate, replaceProductsWithEquivalents, dispatch]);

  const cancelStoreChange = useCallback(() => {
    setIsChangeStoreConfirmationModalOpen(false);
    setStoreCandidate(undefined);
  }, []);

  return {
    storeCandidate,
    handleStoreSelection,
    confirmStoreChange,
    cancelStoreChange,
    isChangeStoreConfirmationModalOpen,
  };
};
